// client/src/coms/views/chatview_parts/messageActions.js
export function buildSharedMessageActions({
  quoteMessage,
  reactToMessage,
  canAddReactions = true,
  beginEditMessage,
  deleteMessage,
  togglePinnedMessage,
  toggleSuperPinnedMessage,
  canEditMessage,
  canDeleteMessage,
  canPinMessage,
  canSuperPinMessage,
  editBusyMessageId,
  deleteBusyMessageId,
  pinBusyMessageId,
  superPinBusyMessageId,
  reportMessage,
  // Optional: which messages can be reported here (DMs: only end-to-end encrypted ones).
  canReportMessage,
  myUserId,
  bookmarkMessage,
  // Archived channels: only reading actions (bookmark, report) remain.
  readOnly = false,
  // Opens the interaction dialog prefilled from a message (poll, suggestion, task ...); null hides the action.
  convertMessage = null,
}) {
  const isInteraction = (raw) => String((raw && raw.system_kind) || '') === 'interaction';
  function isPinned(raw) {
    const m = raw && typeof raw === 'object' ? raw : {};
    return Number(m.is_pinned) === 1;
  }

  function isSuperPinned(raw) {
    const m = raw && typeof raw === 'object' ? raw : {};
    return Number(m.is_super_pinned) === 1 || Number(m.is_header_message) === 1 || Number(m.is_motd) === 1;
  }

  function isBusy(m) {
    const id = String((m && m.id) || '');
    return (
      id === String(editBusyMessageId || '') ||
      id === String(deleteBusyMessageId || '') ||
      id === String(pinBusyMessageId || '') ||
      id === String(superPinBusyMessageId || '')
    );
  }

  return [
    {
      id: 'react',
      label: 'React',
      title: 'React',
      icon: '👍',
      show() {
        return !readOnly && typeof reactToMessage === 'function' && canAddReactions;
      },
      disabled(m) {
        return isBusy(m);
      },
      onClick(m) {
        if (typeof reactToMessage === 'function' && m && m.raw) reactToMessage(m.raw);
      },
    },
    {
      id: 'quote',
      label: 'Quote',
      title: 'Quote',
      icon: '❝',
      show() {
        return !readOnly;
      },
      disabled(m) {
        return isBusy(m);
      },
      onClick(m) {
        if (typeof quoteMessage === 'function') quoteMessage(m);
      },
    },
    {
      id: 'bookmark',
      label: 'Save',
      title: 'Save bookmark',
      icon: '🔖',
      show(m) {
        return typeof bookmarkMessage === 'function' && !!(m && m.raw && Number(m.raw.id) > 0);
      },
      disabled(m) {
        return isBusy(m);
      },
      onClick(m) {
        if (typeof bookmarkMessage === 'function' && m && m.raw) bookmarkMessage(m.raw);
      },
    },
    {
      id: 'pin',
      label: 'Pin',
      title(m) {
        return isPinned(m && m.raw) ? 'Remove pin' : 'Pin message';
      },
      icon: '📌',
      show(m) {
        return !readOnly && !!(m && m.raw && typeof canPinMessage === 'function' && canPinMessage(m.raw));
      },
      active(m) {
        return !!(m && m.raw && isPinned(m.raw));
      },
      disabled(m) {
        return isBusy(m);
      },
      onClick(m) {
        if (typeof togglePinnedMessage === 'function' && m && m.raw) togglePinnedMessage(m.raw);
      },
    },
    {
      id: 'super-pin',
      label: 'Header',
      title(m) {
        return isSuperPinned(m && m.raw) ? 'Remove header message' : 'Set header message';
      },
      icon: '📣',
      show(m) {
        return !readOnly && !!(m && m.raw && typeof canSuperPinMessage === 'function' && canSuperPinMessage(m.raw));
      },
      active(m) {
        return !!(m && m.raw && isSuperPinned(m.raw));
      },
      disabled(m) {
        return isBusy(m);
      },
      onClick(m) {
        if (typeof toggleSuperPinnedMessage === 'function' && m && m.raw) toggleSuperPinnedMessage(m.raw);
      },
    },
    {
      id: 'edit',
      label: 'Edit',
      title: 'Edit',
      icon: '✏️',
      show(m) {
        return !readOnly && !!(m && m.raw && !isInteraction(m.raw) && typeof canEditMessage === 'function' && canEditMessage(m.raw));
      },
      active(m, ctx) {
        return String((ctx && ctx.editingMessageId) || '') === String((m && m.id) || '');
      },
      disabled(m) {
        return isBusy(m);
      },
      onClick(m) {
        if (typeof beginEditMessage === 'function' && m && m.raw) beginEditMessage(m.raw);
      },
    },
    {
      id: 'make-interaction',
      label: 'Make poll / task',
      title: 'Turn into a poll, suggestion or task (the message stays)',
      icon: '📊',
      show(m) {
        return !readOnly && typeof convertMessage === 'function' && !!(m && m.raw && Number(m.raw.id) > 0 && !isInteraction(m.raw) && String(m.raw.body || '').trim());
      },
      disabled(m) {
        return isBusy(m);
      },
      onClick(m) {
        if (typeof convertMessage === 'function' && m && m.raw) convertMessage(m.raw, m);
      },
    },
    {
      id: 'delete',
      label: 'Delete',
      title: 'Delete',
      icon: '🗑',
      show(m) {
        return !readOnly && !!(m && m.raw && typeof canDeleteMessage === 'function' && canDeleteMessage(m.raw));
      },
      disabled(m) {
        return isBusy(m);
      },
      onClick(m) {
        if (typeof deleteMessage === 'function' && m && m.raw) deleteMessage(m.raw);
      },
    },
    {
      id: 'report',
      label: 'Report',
      title: 'Report to moderators',
      icon: '⚑',
      show(m) {
        const author = m && m.raw ? String(m.raw.user_id || '') : '';
        if (typeof canReportMessage === 'function' && !(m && m.raw && canReportMessage(m.raw))) return false;
        return typeof reportMessage === 'function' && !!author && author !== String(myUserId || '');
      },
      disabled(m) {
        return isBusy(m);
      },
      onClick(m) {
        if (typeof reportMessage === 'function' && m && m.raw) reportMessage(m.raw);
      },
    },
  ];
}